<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import { useAuthStore } from '@stores/auth.ts'
  import { useDate, useDisplay } from 'vuetify'
  import { useI18n } from 'vue-i18n'
  import type { Chat } from '@/types'

  import searchIcon from '@assets/icons/search.svg'
  import chatIcon from '@assets/icons/sidebar/new-chat.svg'
  import proIcon from '@assets/icons/sidebar/sina-pro.svg'
  import consultIcon from '@assets/icons/sidebar/consult-doctor.svg'
  import { computed, nextTick, ref, useTemplateRef } from 'vue'
  import Logo from '@assets/logo/icon.svg'
  import SidebarIcon from '@assets/icons/sidebar.svg'
  import { getChatGroupTitle } from '@/utils/date.ts'

  const title = ref<string | null>(null)
  const { mobile } = useDisplay()
  const { t } = useI18n()
  const dateAdapter = useDate()

  const authStore = useAuthStore()
  const paymentDialog = usePaymentDialog()
  const authDialog = useAuthDialog()
  const { isAuthenticated } = storeToRefs(authStore)
  const chatStore = useChatStore()
  const model = ref<boolean>(!mobile.value)
  const search = useTemplateRef('search')

  const consultationStore = useConsultationsStore()
  const { pendingRecommendation } = storeToRefs(consultationStore)

  const rail = ref<boolean>(!mobile.value)
  const collapsed = computed(() => {
    return mobile.value ? false : rail.value
  })

  const emits = defineEmits<{
    'start-new-chat': [consultationPrompt: boolean]
    'show-logout': []
  }>()

  const groupedChats = computed(() => {
    const groups: Record<string, Chat[]> = {}

    chatStore.chats.forEach(chat => {
      const groupTitle = getChatGroupTitle(chat.updated_at ?? chat.created_at, t, dateAdapter)
      if (!groups[groupTitle]) {
        groups[groupTitle] = []
      }
      groups[groupTitle].push(chat)
    })

    return groups
  })

  const showDrawer = () => {
    if (mobile.value) {
      model.value = !model.value
      if (!model.value) title.value = null
    } else {
      rail.value = !rail.value
    }

    useTrackStore().track('side_menu_clicked')
  }

  const focusInput = async () => {
    showDrawer()
    await nextTick()
    search?.value?.focusInput()
  }

  watch(() => collapsed.value, newValue => {
    if (newValue) title.value = null
  })

  defineExpose({
    showDrawer,
  })
</script>

<template>
  <v-navigation-drawer
    v-model="model"
    class="bg-grey-900 overflow-y-hidden"
    :permanent="!mobile"
    :rail="rail"
    :rail-width="68"
    :temporary="mobile"
    :width="320"
    @click="rail = false"
  >
    <v-col class="d-flex justify-space-between ga-6 pt-4 px-4 pb-5 flex-grow-0" :class="{'drawer-opener':rail}">
      <v-badge
        class="hide-on-hover"
        color="error"
        dot
        location="top end"
        :model-value="!!pendingRecommendation"
      >
        <v-icon
          class="text-primary flex-shrink-0"
          :icon="Logo"
          size="36"
        />
      </v-badge>
      <v-btn
        class="chat-toggle-btn"
        density="compact"
        flat
        icon
        size="36"
        @click.stop="showDrawer"
      >
        <v-icon
          class="open-icon text-grey-400"
          :icon="SidebarIcon"
          size="36"
        />
      </v-btn>
    </v-col>

    <search-chats
      v-if="isAuthenticated"
      ref="search"
      v-model="title"
      :collapsed="collapsed"
      :mobile="mobile"
    />

    <v-col v-if="!title" class="d-flex flex-column overflow-y-hidden pa-0">
      <!-- Actions List   -->
      <v-list
        class="pt-0 px-0 pb-3"
        :class="{
          'mx-4 mt-5': !collapsed
        }"
        density="compact"
        nav
      >
        <v-list-item
          v-if="isAuthenticated && collapsed"
          :active="false"
          class="mt-1 mb-5 py-2 text-subtitle-2 font-weight-bold"
          :class="{
            'mx-auto custom-btn': collapsed,
            'px-3 text-grey-400': !collapsed
          }"
          rounded="lg"
          @click.stop="focusInput"
        >
          <template #prepend>
            <v-icon
              class="text-grey-50"
              :icon="searchIcon"
              size="24"
            />
            <v-tooltip
              v-if="collapsed"
              activator="parent"
              content-class="bg-grey-900 text-caption font-weight-bold rounded-pill"
              location="end"
            >
              {{ t('search') }}
            </v-tooltip>
          </template>
        </v-list-item>
        <v-list-item
          :active="false"
          class="py-2"
          :class="{
            'mx-auto custom-btn': collapsed,
            'px-3 text-grey-400': !collapsed
          }"
          rounded="lg"
          @click.stop="emits('start-new-chat', false)"
        >
          <template #prepend>
            <v-icon
              class="text-grey-50"
              :icon="chatIcon"
              size="24"
            />
            <v-tooltip
              v-if="collapsed"
              activator="parent"
              content-class="bg-grey-900 text-caption font-weight-bold rounded-pill"
              location="end"
            >
              {{ t('new_chat') }}
            </v-tooltip>
          </template>
          <v-list-item-title v-if="!collapsed" class="text-grey-50 font-weight-bold text-subtitle-2">
            <i18n-t keypath="new_chat" />
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="isAuthenticated && ! authStore.isSubscribed"
          v-track="{event:'sina_pro_clicked',eventData: {source: 'side_menu'}}"
          :active="false"
          class="py-2"
          :class="{
            'mx-auto custom-btn': collapsed,
            'px-3 text-grey-400': !collapsed
          }"
          rounded="lg"
          @click="paymentDialog.show()"
        >
          <template #prepend>
            <v-icon
              class="text-grey-50"
              :icon="proIcon"
              size="24"
            />
            <v-tooltip
              v-if="collapsed"
              activator="parent"
              content-class="bg-grey-900 text-caption font-weight-bold rounded-pill"
              location="end"
            >
              <i18n-t keypath="sina_pro">
                <template #pro>
                  <span class="text-sina-pro">
                    <i18n-t keypath="pro" />
                  </span>
                </template>
              </i18n-t>
            </v-tooltip>
          </template>
          <v-list-item-title v-if="!collapsed" class="text-grey-50 font-weight-bold text-subtitle-2">
            <i18n-t keypath="sina_pro">
              <template #pro>
                <span class="text-sina-pro">
                  <i18n-t keypath="pro" />
                </span>
              </template>
            </i18n-t>
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-track="{event:'consultation_link_clicked',eventData: {source: 'side_menu'}}"
          :active="false"
          class="py-2"
          :class="{
            'mx-auto custom-btn': collapsed,
            'px-3 text-grey-400': !collapsed
          }"
          rounded="lg"
          :to="{path:'/',query:{ c:'true'}, force:true}"
          @click.stop="emits('start-new-chat', true)"
        >
          <template #prepend>
            <v-icon
              class="text-grey-50"
              :icon="consultIcon"
              size="24"
            />
            <v-tooltip
              v-if="collapsed"
              activator="parent"
              content-class="bg-grey-900 text-caption font-weight-bold rounded-pill"
              location="end"
            >
              {{ t('talk_to_doctor_now') }}
            </v-tooltip>
          </template>
          <v-list-item-title v-if="!collapsed" class="text-grey-50 font-weight-bold text-subtitle-2">
            <i18n-t keypath="talk_to_doctor_now" />
          </v-list-item-title>
        </v-list-item>
      </v-list>

      <!-- Chats List   -->
      <div
        v-if="!collapsed"
        class="chat-drawer-content pt-2 pb-2 ps-4 pe-3 flex-grow-1"
        :class="{'opacity-0': collapsed }"
      >
        <p v-if="!isAuthenticated" class="text-caption text-grey-500 font-weight-medium mb-2 px-2">
          {{ t('chat_history') }}
        </p>
        <div v-if="isAuthenticated && chatStore.chats.length > 0" class="chat-list">
          <div v-for="(groupChats, groupTitle) in groupedChats" :key="groupTitle" class="mb-2">
            <h3 class="text-subtitle-2 text-grey-500 font-weight-medium mb-2 px-2">
              {{ groupTitle }}
            </h3>
            <chat-history-side-menu :chats="groupChats" />
          </div>
        </div>
        <div v-else-if="!isAuthenticated" class="mt-4 mx-4">
          <v-card border class="bg-grey-800 border-opacity-75 rounded-xl pa-4" flat>
            <p class="text-body-1 font-weight-bold mb-2 text-grey-50">
              {{ t('chat_history_login_title') }}
            </p>
            <p class="text-caption text-grey-400 mb-4">
              {{ t('chat_history_login_subtitle') }}
            </p>
            <v-btn
              block
              class="font-weight-bold"
              color="primary"
              flat
              height="40"
              rounded="lg"
              @click="authDialog.show()"
            >
              {{ t('login_or_register') }}
            </v-btn>
          </v-card>
        </div>
      </div>
      <v-spacer v-else />

      <!-- Download App -->
      <download-app-banner
        class="pt-3 pb-3 flex-grow-0"
        :collapsed="collapsed"
        :mobile="mobile"
      />
    </v-col>

    <v-col
      v-if="isAuthenticated && !title"
      class="flex-grow-0 pt-1"
      :class="{
        'px-3': !collapsed,
        'px-0 mx-2': collapsed
      }"
    >
      <user-main-menu
        :collapsed="collapsed"
        @show-logout="emits('show-logout')"
      />
    </v-col>
  </v-navigation-drawer>
</template>

<style scoped lang="scss">
.custom-btn {
  width: 40px;
  max-width: 40px;
  min-width: 40px;
}

:deep(.v-list-item__prepend) {
  width: 36px;
}

:deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
}

.chat-drawer-content {
  height: 30%;
  overflow-y: scroll;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */


  .empty-state {
    height: calc(100% - 60px);
    opacity: 0.7;
  }
}

.drawer-opener:hover {
  .hide-on-hover {
    display: none;
  }

  .open-icon {
    transform: rotate(180deg);
  }
}

</style>
